import { useCallback, useState } from 'react';
import { useApi, fetchApiRef } from '@backstage/frontend-plugin-api';
import {
  TRACK_IMPORT_BATCH_SIZE,
  type TrackImportResult,
} from '@internal/backstage-plugin-pulse-common';
import { trackImportResultSchema } from '../schemas/tracks';
import { invalidateTracksCache } from './cache';

const errorFromResponse = async (response: Response): Promise<Error> => {
  try {
    const data = await response.json();
    return new Error(data.error ?? `Track import failed: ${response.status}`);
  } catch {
    return new Error(`Track import failed: ${response.status}`);
  }
};

type FailedTrackImportBatch = {
  offset: number;
  size: number;
};

export class TrackImportBatchError extends Error {
  readonly partialResult: TrackImportResult;
  readonly failedBatch: FailedTrackImportBatch;

  constructor(
    message: string,
    partialResult: TrackImportResult,
    failedBatch: FailedTrackImportBatch,
  ) {
    super(message);
    this.name = 'TrackImportBatchError';
    this.partialResult = partialResult;
    this.failedBatch = failedBatch;
  }
}

export const isTrackImportBatchError = (
  error: unknown,
): error is TrackImportBatchError =>
  error instanceof Error &&
  error.name === 'TrackImportBatchError' &&
  'partialResult' in error &&
  'failedBatch' in error;

/** Imports parsed YAML track definitions as one partial-success batch. */
export const useImportTracks = () => {
  const { fetch } = useApi(fetchApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const clearError = useCallback((): void => setError(null), []);

  const importTracks = useCallback(
    async (tracks: unknown[]): Promise<TrackImportResult> => {
      setLoading(true);
      setError(null);
      const result: TrackImportResult = {
        total: 0,
        imported: [],
        skipped: [],
        failed: [],
      };
      try {
        for (
          let offset = 0;
          offset < Math.max(tracks.length, 1);
          offset += TRACK_IMPORT_BATCH_SIZE
        ) {
          const batch = tracks.slice(offset, offset + TRACK_IMPORT_BATCH_SIZE);
          const response = await fetch('plugin://pulse/tracks/import', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tracks: batch }),
          });
          if (!response.ok) {
            const responseError = await errorFromResponse(response);
            throw new TrackImportBatchError(responseError.message, result, {
              offset,
              size: batch.length,
            });
          }

          const batchResult = trackImportResultSchema.parse(
            await response.json(),
          );
          result.total += batchResult.total;
          result.imported.push(...batchResult.imported);
          result.skipped.push(...batchResult.skipped);
          result.failed.push(...batchResult.failed);
        }

        return result;
      } catch (error_) {
        const importError =
          error_ instanceof Error ? error_ : new Error('Track import failed');
        setError(importError);
        throw importError;
      } finally {
        if (result.imported.length > 0) {
          invalidateTracksCache();
        }
        setLoading(false);
      }
    },
    [fetch],
  );

  return { importTracks, loading, error, clearError };
};
